<template>
  <!-- Desktop -->
  <div class="hidden sm:flex sm:flex-col sm:h-screen bg-white dark:bg-[#0d1117]">
    <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between px-6 py-3.5">
        <h1 class="text-xl font-bold text-gray-900 dark:text-white">Tasks</h1>
        <div class="flex items-center gap-3">
          <div class="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            <button
              v-for="v in VIEWS" :key="v.value"
              @click="view = v.value"
              class="px-3 py-1 text-xs font-semibold rounded-md transition-colors"
              :class="view === v.value ? 'bg-white dark:bg-[#1C2128] text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'"
            >{{ v.label }}</button>
          </div>
          <button
            v-if="view !== 'stats'"
            @click="onNewTask"
            class="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
          >New task</button>
        </div>
      </div>
    </div>
    <div class="flex-1 overflow-hidden">
      <TasksChecklist v-if="view === 'checklist'" ref="desktopChecklistRef" />
      <TasksBoard v-else-if="view === 'board'" ref="desktopBoardRef" />
      <TasksStats v-else />
    </div>
  </div>

  <!-- Mobile: Material-style top app bar (elevation, not a border; a
       sliding-indicator tab strip, not floating pills) + a Floating Action
       Button for "New" instead of a header button — the header pill/pill-tab
       combo read as a shrunk desktop bar, not an Android surface. -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-screen">
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] shadow-[0_1px_2px_rgba(0,0,0,0.06),0_2px_6px_rgba(0,0,0,0.05)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_2px_6px_rgba(0,0,0,0.3)]">
      <div class="flex items-center px-4 h-14">
        <h1 class="text-xl font-medium text-gray-900 dark:text-white tracking-tight">Tasks</h1>
      </div>
      <div class="relative flex">
        <button
          v-for="v in VIEWS" :key="v.value"
          @click="view = v.value"
          class="flex-1 py-3 text-[13px] font-semibold tracking-wide uppercase transition-colors active:bg-black/[0.03] dark:active:bg-white/[0.05]"
          :class="view === v.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'"
        >{{ v.label }}</button>
        <span
          class="absolute bottom-0 h-[3px] rounded-t-full bg-blue-600 dark:bg-blue-400 transition-[left] duration-300 ease-out"
          :style="{ width: `${100 / VIEWS.length}%`, left: `${activeIndex * (100 / VIEWS.length)}%` }"
        ></span>
      </div>
    </div>
    <div class="pb-20">
      <TasksChecklist v-if="view === 'checklist'" ref="mobileChecklistRef" variant="mobile" />
      <TasksBoard v-else-if="view === 'board'" ref="mobileBoardRef" variant="mobile" />
      <TasksStats v-else />
    </div>

    <!-- FAB — the Android-native way to expose "create," not a header pill -->
    <button
      v-if="view !== 'stats'"
      @click="onNewTask"
      aria-label="New task"
      class="fixed bottom-20 right-4 z-30 w-14 h-14 rounded-full bg-blue-600 text-white shadow-[0_3px_5px_rgba(0,0,0,0.2),0_7px_12px_rgba(0,0,0,0.15)] active:scale-95 active:shadow-[0_2px_3px_rgba(0,0,0,0.2)] flex items-center justify-center transition-transform duration-150"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="2.25" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
      </svg>
    </button>
  </div>

</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useGlobalStore } from 'stores/global.js'
import TasksChecklist from './TasksChecklist.vue'
import TasksBoard from './TasksBoard.vue'
import TasksStats from './TasksStats.vue'

const VIEWS = [
  { value: 'checklist', label: 'Checklist' },
  { value: 'board', label: 'Board' },
  { value: 'stats', label: 'Stats' },
]

// window.CURRENT_USER (the initial page-load hydration — see
// ApplicationHelper) never includes date_format/timezone/time_format;
// globalStore.init() only pre-fills them from a localStorage cache
// (cal_user_prefs) written by the *last* successful refreshUser() call.
// That cache can go stale — change your date format in Settings, come
// straight to /tasks without anything re-fetching in between, and every
// due-date label here would keep reading the old preference. Unconditional
// (not "only if missing" — the cache is populated, just possibly wrong)
// refresh on mount so every due date shown on this page reflects the
// account's *current* date_format, not a stale local copy.
const globalStore = useGlobalStore()
onMounted(() => { globalStore.refreshUser() })

const VIEW_KEY = 'tasks_view'
const view = ref(localStorage.getItem(VIEW_KEY) === 'board' ? 'board' : 'checklist')

watch(view, (v) => localStorage.setItem(VIEW_KEY, v))

// Position of the mobile tab strip's sliding underline indicator.
const activeIndex = computed(() => VIEWS.findIndex(v => v.value === view.value))

// Desktop/mobile instances of both views are always mounted (shown/hidden
// purely via responsive CSS, this app's usual pattern — see CLAUDE.md), so
// there are two refs per view. Only one is ever visible at a time;
// triggering startAdd() on both is harmless since each keeps its own local
// add-row state. There's no standalone create page anymore — "New task"
// always opens the relevant view's own inline quick-add (checklist: a row
// at the top of the list; board: the Pending column's "+ Add task" row).
const desktopChecklistRef = ref(null)
const mobileChecklistRef = ref(null)
const desktopBoardRef = ref(null)
const mobileBoardRef = ref(null)

function onNewTask() {
  if (view.value === 'checklist') {
    desktopChecklistRef.value?.startAdd()
    mobileChecklistRef.value?.startAdd()
  } else {
    desktopBoardRef.value?.startAdd('pending')
    mobileBoardRef.value?.startAdd('pending')
  }
}
</script>
